import * as Effects from 'redux-saga/effects'
import { all, call, put, take } from 'redux-saga/effects'
import { EmployeesAction } from '../../reducers/employees/actionNames'
import fetchActionsName, { ActionNames } from '../../utils/FetchActionsName'
import { callFetch } from '../../actions/fetch'
import createEmployee from '../../models/Employees/createEmployee'
import {
  fireToastError,
  fireToastSuccess,
} from '../../actions/toasts/fireToast'
import i18n from '../../i18n'
import fetchEmployees from '../../models/Employees/fetchEmployees'
import deleteEmployee from '../../models/Employees/deleteEmployee'
import updateEmployee from '../../models/Employees/updateEmployee'
import { Mixpanel } from '../../utils/Mixpanel'
import { getUserId } from '../../reducers/userAccount/selectors/userAccount'
import {
  mapScopeGroupToENString,
  PermissionScope,
} from '../../containers/Employees'

import { getEmployees as getEmployeesSelector } from '../../reducers/employees/selectors'

function* addOrUpdate(action) {
  const userId = yield Effects.select(getUserId)
  const workers = yield Effects.select(getEmployeesSelector)

  if (action.payload.id) {
    try {
      const actionNames = fetchActionsName(ActionNames.UpdateEmployee)

      const [{ service }] = yield all([
        take(actionNames.output),
        put(callFetch(actionNames.input, actionNames.output, updateEmployee)),
      ])

      let mappedScope = action.payload.scope
        ? Object.entries(action.payload.scope)
            .filter(([key, value]) => Boolean(value))
            .map(([key, _]) => key)
        : []

      if (mappedScope.includes('SCOPE_HIDE_CLIENT_DATA')) {
        mappedScope.splice(mappedScope.indexOf('SCOPE_HIDE_CLIENT_DATA'), 1)
      } else {
        mappedScope = [...mappedScope, 'SCOPE_HIDE_CLIENT_DATA']
      }

      const worker = yield call(service, {
        id: action.payload.id,
        email: action.payload.email,
        name: action.payload.name,
        surname: action.payload.surname,
        phone: action.payload.phone,
        notes: action.payload.notes,
        scope_group: action.payload.scope_group,
        scopes: mappedScope,
      })

      Mixpanel.track('Employees List Edit Employee Saved', {
        'Employee ID': worker.items.id,
        'Employee Name': worker.items.name + ' ' + worker.items.surname,
        'Employee Email': worker.items.email,
        'Employee Role': mapScopeGroupToENString(worker.items.scope_group),
        'Permission Premium': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_BUY_PREMIUM,
        ),
        'Permission Reservation Search': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_SEARCH,
        ),
        'Permission Room Management': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_EDIT_ROOMS,
        ),
        'Permission Sales Channels Synch': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_SYNC_INTEGRATIONS,
        ),
        'Permission Reservation Form Preview': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_VIEW_RESERVATIONS,
        ),
        'Permission Personal Data Of The Guests': !worker.items.scopes.includes(
          PermissionScope.SCOPE_HIDE_CLIENT_DATA,
        ),
        'Permission Reservation List': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_VIEW_LIST_RESERVATIONS,
        ),
        'Permission Reservation System': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_WIDGET,
        ),
        'Permission Export Bookings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_EXPORT,
        ),
        'Permission Tasks Departure And Arrival': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_INCOMING,
        ),
        'Permission Tasks Cleaning': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_CLEANING,
        ),
        'Permission Tasks Meals': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_FOOD,
        ),
        'Permission Tasks Lack Of Payment': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_PAYMENTS,
        ),
        'Permission Clients': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_LIST_CLIENTS,
        ),
        'Permission Profile': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PROFILE,
        ),
        'Permission Prices And Reservation Restrictions': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PRICING_AND_RESTRICTIONS,
        ),
        'Permission Price List Settings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS,
        ),
        'Permission Invoices And Bills': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_INVOICES,
        ),
        'Permission Invoices And Bills Settings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_INVOICES_SETTINGS,
        ),
        'Permission Financial Report': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
        ),
        'Permission Occupancy Report': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_OCCUPANCY_REPORTS,
        ),
        'Permission Sales Channels': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
        ),
        'Permission Global System Settings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_GLOBAL_SETTINGS,
        ),
        'Permission Message Templates': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TEMPLATES,
        ),
        'Permission Add Permissions': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PERMISSIONS,
        ),
      })

      Mixpanel.people.increment(userId, 'Employees Edited', 1)

      const w = workers.find(w => w.id === action.payload.id)

      if (w && w.email !== action.payload.email) {
        yield Effects.put(
          fireToastSuccess(i18n.t('BED_3610_invitation_sent'), 10000),
        )
      } else {
        yield Effects.put(fireToastSuccess(i18n.t('BED_3610_worker_altered')))
      }
      yield Effects.put({ type: EmployeesAction.SetTab, payload: 0 })
      yield Effects.put({ type: EmployeesAction.FetchEmployees })
    } catch (e) {
      if (e.message === 'User already exists') {
        yield Effects.put(fireToastError(i18n.t('reg_error_user_exists')))
        yield Effects.put({
          type: EmployeesAction.SetEmailError,
          payload: 'BED_3610_user_in_database',
        })
      }
      console.error(e)
    }
  } else {
    try {
      const actionNames = fetchActionsName(ActionNames.CreateEmployee)
      const [{ service }] = yield all([
        take(actionNames.output),
        put(callFetch(actionNames.input, actionNames.output, createEmployee)),
      ])

      let mappedScope = action.payload.scope
        ? Object.entries(action.payload.scope)
            .filter(([key, value]) => Boolean(value))
            .map(([key, _]) => key)
        : []

      if (mappedScope.includes('SCOPE_HIDE_CLIENT_DATA')) {
        mappedScope.splice(mappedScope.indexOf('SCOPE_HIDE_CLIENT_DATA'), 1)
      } else {
        mappedScope = [...mappedScope, 'SCOPE_HIDE_CLIENT_DATA']
      }

      const worker = yield call(service, {
        email: action.payload.email,
        name: action.payload.name,
        surname: action.payload.surname,
        phone: action.payload.phone,
        notes: action.payload.notes,
        scope_group: action.payload.scope_group,
        scopes: mappedScope,
      })

      Mixpanel.track('Employee List New Employee Created', {
        'Employee ID': worker.items.id,
        'Employee Name': worker.items.name + ' ' + worker.items.surname,
        'Employee Email': worker.items.email,
        'Employee Role': mapScopeGroupToENString(worker.items.scope_group),
        'Permission Premium': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_BUY_PREMIUM,
        ),
        'Permission Reservation Search': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_SEARCH,
        ),
        'Permission Room Management': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_EDIT_ROOMS,
        ),
        'Permission Sales Channels Synch': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_SYNC_INTEGRATIONS,
        ),
        'Permission Reservation Form Preview': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_VIEW_RESERVATIONS,
        ),
        'Permission Personal Data Of The Guests': !worker.items.scopes.includes(
          PermissionScope.SCOPE_HIDE_CLIENT_DATA,
        ),
        'Permission Reservation List': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_VIEW_LIST_RESERVATIONS,
        ),
        'Permission Reservation System': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_WIDGET,
        ),
        'Permission Export Bookings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_EXPORT,
        ),
        'Permission Tasks Departure And Arrival': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_INCOMING,
        ),
        'Permission Tasks Cleaning': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_CLEANING,
        ),
        'Permission Tasks Meals': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_FOOD,
        ),
        'Permission Tasks Lack Of Payment': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TASKS_PAYMENTS,
        ),
        'Permission Clients': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_LIST_CLIENTS,
        ),
        'Permission Profile': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PROFILE,
        ),
        'Permission Prices And Reservation Restrictions': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PRICING_AND_RESTRICTIONS,
        ),
        'Permission Price List Settings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS,
        ),
        'Permission Invoices And Bills': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_INVOICES,
        ),
        'Permission Invoices And Bills Settings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_INVOICES_SETTINGS,
        ),
        'Permission Financial Report': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_FINANCIAL_REPORTS,
        ),
        'Permission Occupancy Report': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_OCCUPANCY_REPORTS,
        ),
        'Permission Sales Channels': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_INTEGRATIONS_SETTINGS,
        ),
        'Permission Global System Settings': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_GLOBAL_SETTINGS,
        ),
        'Permission Message Templates': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_TEMPLATES,
        ),
        'Permission Add Permissions': worker.items.scopes.includes(
          PermissionScope.SCOPE_CAN_USE_PERMISSIONS,
        ),
      })

      Mixpanel.people.increment(userId, 'Employees Created', 1)

      yield Effects.put(
        fireToastSuccess(i18n.t('BED_3610_invitation_sent'), 10000),
      )
      yield Effects.put({ type: EmployeesAction.SetTab, payload: 0 })
      yield Effects.put({ type: EmployeesAction.FetchEmployees })
    } catch (e) {
      if (e.message === 'User already exists') {
        yield Effects.put(fireToastError(i18n.t('reg_error_user_exists')))
        yield Effects.put({
          type: EmployeesAction.SetEmailError,
          payload: 'BED_3610_user_in_database',
        })
      }
      console.error(e)
    }
  }
}

function* getEmployees() {
  try {
    const actionNames = fetchActionsName(ActionNames.FetchEmployees)
    const [{ service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, fetchEmployees)),
    ])

    const employees = yield call(service)

    yield Effects.put({
      type: EmployeesAction.SetEmployees,
      payload: employees.items
        .map(i => {
          const obj = {}

          try {
            JSON.parse(i.scopes).forEach(s => (obj[s] = true))

            if (obj['SCOPE_HIDE_CLIENT_DATA']) {
              obj['SCOPE_HIDE_CLIENT_DATA'] = false
            } else {
              obj['SCOPE_HIDE_CLIENT_DATA'] = true
            }

            return {
              ...i,
              scope: obj,
            }
          } catch (e) {
            console.error(e)
            return {
              ...i,
              scope: {
                SCOPE_HIDE_CLIENT_DATA: true,
              },
            }
          }
        })
        .reverse(),
    })
  } catch (e) {
    console.error(e)
  }
}

function* deleteEmp(action) {
  try {
    const actionNames = fetchActionsName(ActionNames.DeleteEmployee)
    const [{ service }] = yield all([
      take(actionNames.output),
      put(callFetch(actionNames.input, actionNames.output, deleteEmployee)),
    ])

    yield call(service, {
      id: action.payload,
    })
    yield Effects.put(fireToastSuccess(i18n.t('BED_3607_user_deleted_baner')))
    yield Effects.put({ type: EmployeesAction.FetchEmployees })
  } catch (e) {
    console.error(e)
  }
}
export default function*() {
  yield Effects.takeEvery(EmployeesAction.AddOrUpdateEmployee, addOrUpdate)
  yield Effects.takeEvery(EmployeesAction.FetchEmployees, getEmployees)
  yield Effects.takeEvery(EmployeesAction.DeleteEmployee, deleteEmp)
}
